import * as React from 'react';
import styled from 'styled-components';
import { animated } from 'react-spring';

import { RouteChildrenProps, useParams } from 'react-router';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';
import { Seo } from '../../../../utils/seo/Index';
import { Container } from '../../../layout/Container';
import { Section } from '../../../layout/Section';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { AccordionBenefits } from '../../../partials/AccordionBenefits';
import { Thanks } from '../../../layout/Thanks';
import { AccordionGeneral } from '../../../partials/AccordionGeneral';
import { BackgroundLoad } from '../../../layout/BackgroundLoad';
import { AboutInflationText } from '../../../partials/AboutInflationText';
import { createLeadMutation } from '../../../../../client/__graphql__/mutations';
import { useMutation } from '@apollo/client';
import { ILeadInput, ILeadResponse } from '../../../../__types__/global';
import { useForm } from '../../../../utils/hooks/useForm';
import { Form, FormRow } from '../../../ui/form/Form';
import { FormError } from '../../../ui/form/FormError';
import { Input } from '../../../ui/form/Input';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';
import { ValuationForm } from '../../../partials/forms/Valuation';
import { useValuationContext } from '../../../../utils/hooks/useValuationContext';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { ArticleHighlight } from './ArticleHighlight';
import { useInflationContext } from '../../../../utils/hooks/useInflationContext';
import { routeConfig } from '../../../../__config__/routes';
import { Mail, MailVariables } from '../../../../__types__/generated/Mail';
import { MAIL_MUTATION } from '../../../../../client/__graphql__/mutations/mail';
import { Consent } from '../../../ui/form/Consent';

const Main: React.FC<RouteChildrenProps> = () => {
  const { articles } = useInflationContext();
  const allArticles = articles();
  const [posted, setPosted] = React.useState(false);

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [mail, { loading, error }] = useMutation<Mail, MailVariables>(
    MAIL_MUTATION,
    {
      onCompleted: ({ lead }) => {
        if (lead && lead.mail && lead.mail.success) {
          window.scroll(0, 0);
          setPosted(true);
        }
      }
    }
  );

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: '1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: '00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
      /*       {
        type: 'email',
        name: 'email',
        placeholder: 'ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      } */
    ],
    submit: () => {
      const preparedFields = getFields(fields);
      mail({
        variables: {
          input: {
            ...preparedFields,
            referer: location.pathname,
            leadType: LeadType.EPOST,
            mailType: 68
          }
        }
      });
    }
  });

  return (
    <>
      <Seo
        title="Tunneleffekten - PrivatMegleren"
        description="Nå øker verdien på eiendommer i Bjørnafjorden"
        image="https://cdn.reeltime.no/pm_assets/img/headsup/mansion.jpg"
      />
      <Container style={{ paddingTop: '100px', ...pageFadeIn }}>
        <FormWrapper style={fadeOut}>
          <Section className="form-section" style={{ paddingBottom: '10px' }}>
            <Heading tag="h1" center={true}>
              Sjekk tunneleffekten
            </Heading>
            <Paragraph center={true} className="form-text">
              Nye E39 har en positiv effekt på Bjørnafjordens utvikling. I en
              tid der det har vært mye usikkerhet rundt boligmarkedet er vi så
              heldige å ha tunelleffekten. For mange vil det bety fremdeles god
              verdi på bolig, fritidsbolig eller tomt. Vet du hva din eiendom er
              verdt nå?
            </Paragraph>
            {/*             <Paragraph center={true} className="form-text">
              Lurer du på hva din eiendom er verdt? Fyll inn skjema under og en
              eiendomsmegler kontakter deg. En verdivurdering er ikke bare
              nødvendig når du skal selge eiendom. Den er også påkrevd fra
              banken, dersom du ønsker å refinansiere boliglånet for å få bedre
              betingelser.
            </Paragraph> */}
            <Heading style={{ paddingTop: '1em' }} tag="h2" center={true}>
              Hvorfor øker verdien?
            </Heading>
            <Paragraph center={true} className="form-text">
              I oktober åpnet endelig nye E39. Kjøreturen fra Bjørnafjorden til
              Bergen sentrum ble vesentlig kortere, og flere har fått øynene opp
              for vår vakre kommune. Veien åpner for muligheter i hele kommunen,
              både for økt tilflytting, utbygging og ny næringsvirksomhet.
              Summen av dette gjør Bjørnafjorden mer attraktivt for de som er i
              eiendomsmarkedet, noe som igjen vil påvirke prisen på eiendommer i
              vår kommune.
            </Paragraph>
            <Heading style={{ paddingTop: '1em' }} tag="h2" center={true}>
              Hvordan påvirker det deg?
            </Heading>
            <Paragraph center={true} className="form-text">
              Om tunneleffekten har spilt positivt inn på verdien av din
              eiendom, kan det gi deg flere fordeler. Banken gir deg som oftest
              lavere rente på boliglånet, dersom verdien har blitt høyere. De
              fleste får også økt sin belåningsgrad, slik at du kan låne mer om
              du ønsker det.
            </Paragraph>
            <Paragraph center={true} className="form-text">
              Du behøver derfor ikke selge eiendommen din, for å lure på hva den
              er verdt. Er det lenge siden du har fått en verdivurdering på
              boligen din, er det uansett smart å gjøre det nå.
            </Paragraph>
            <Heading style={{ paddingTop: '1em' }} tag="h2" center={true}>
              Tilbud fra Nordea
            </Heading>
            <Paragraph center={true} className="form-text">
              I samarbeid med Nordea har vi gleden av å tilby alle våre kunder
              et uforpliktende kampanjetilbud* i forbindelse med nye E39. Ønsker
              du refinansiering, nytt boliglån eller flytting av det lånet du
              har? Nordea spanderer etableringsgebyr og tilbyr meget gode
              betingelser.
            </Paragraph>
            <Paragraph center={true} className="form-text">
              Lurer du på hva din eiendom er verdt? Fyll inn skjema under og en
              eiendomsmegler kontakter deg.
            </Paragraph>
          </Section>
          {/*        <ValuationForm /> */}
          <Form onSubmit={handleSubmit} noValidate>
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item, index) => {
              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item?.type}
                    name={item?.name}
                    label={item?.label}
                    labelAfter={item?.labelAfter}
                    placeholder={item?.placeholder}
                    value={item?.value}
                    onChange={handleChange}
                    error={item?.error}
                  />
                </FormRow>
              );
            })}

            <Paragraph className="form-text">
              Jeg anmoder PrivatMegleren om å kontakte meg på telefon i form av
              sms eller oppringing.
            </Paragraph>

            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Kontakt meg
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig?.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
            {/*             <Consent fields={consents} handleChange={onChange} /> */}
          </Form>
        </FormWrapper>

        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>

            <Paragraph center={true}>
              En eiendomsmegler vil ta kontakt med deg innen kort tid.
            </Paragraph>
            {/*             <Paragraph center={true}>
              En eiendomsmegler vil ta kontakt med deg innen kort tid for å
              avtale en befaring.
            </Paragraph> */}
            {/*             <Paragraph center={true}>
              I mellomtiden kan du lese mer om salg av eiendom her.
            </Paragraph> */}
            {/*   <AccordionGeneral /> */}
          </Section>
          <Section className="form-section" style={{ paddingTop: 0 }}>
            <AccordionBenefits />
          </Section>
        </Thanks>
      </Container>
      <InflationWrapper>
        <Heading
          style={{ textAlign: 'center', paddingTop: '1em' }}
          className="underline"
          tag="h2"
        >
          Mer fra oss
        </Heading>
        <ArticleHighlight
          title="Stor interesse for familieboliger"
          text={
            'I områdene Nore Neset og Lysefjorden har det vært en økning i antall bergensere på visning.'
          }
          image={
            'https://cdn.reeltime.no/pm_assets/kampanjer/headsup/Vidar-Hjertaas.jpg'
          }
          imageOverlay={''}
          button={{
            label: 'Les mer',
            link: '/kampanje/vikoyr/main/artikkel1'
          }}
        />
        <ArticleHighlight
          className="reverse"
          title="Mye kos i Os"
          text="De siste årene har det vært en økende interesse for dyrere eiendommer i Bjørnafjorden og i Os."
          image={
            'https://cdn.reeltime.no/pm_assets/kampanjer/headsup/Arild-Vikoyr.jpg'
          }
          imageOverlay={''}
          button={{
            label: 'Les mer',
            link: '/kampanje/vikoyr/main/artikkel2'
          }}
        />
      </InflationWrapper>
      <BackgroundLoad
        opacity={0.3}
        url="https://cdn.reeltime.no/pm_assets/img/headsup/mansion.jpg"
        showOnMobile
      />
    </>
  );
};

export default Main;

const FormWrapper = styled(animated.div)``;
const InflationWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.burgundy};
  width: 100%;

  .underline {
    display: flex;
    flex-flow: column;
    position: relative;
    padding-bottom: 25px;
    margin-bottom: 25px;

    ::after {
      position: absolute;
      content: '';
      width: 100px;
      height: 5px;
      bottom: 0;
      left: 0;
      right: 0;
      margin: auto;
      background: ${({ theme }) => theme.colors.coral};
    }
  }
`;
